import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import { listEvowalks, connectEvowalk, disconnectEvowalk, removeEvowalk } from '../../util/recordingAPI';
import { evowalkAddress, evowalkDevicesFromList, evowalkLabel, evowalkRowKey, isNewEvowalkDevice } from '../../util/evowalkDevice';
import Box from "@mui/material/Box";
import Button from '@mui/material/Button';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import Fade from '@mui/material/Fade';
import { IoBatteryFull, IoBatteryHalf, IoBatteryDead } from 'react-icons/io5';
import '../../../dist/assets/stylesheets/css/device.css'
import { isDeviceConnected, rememberConnectedEvowalks } from '../../util/deviceConnectionMemory';

const CommonButton = styled(Button)({
  color: 'black',
  '& svg': {
    fontSize: '1.2rem',
    marginRight: '5px',
  }
});

function EvoWalkPage() {
  const history = useHistory();
  const [isScanning, setIsScanning] = useState(false)
  const [evowalks, setEvowalks] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isConnecting, setIsConnecting] = useState({});
  const [anyConnecting, setAnyConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState({});
  const [isRemoving, setIsRemoving] = useState({});
  const [openDialog, setOpenDialog] = React.useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    handleScan()
  }, [])


  useEffect(() => {
    if (errorMsg) {
      setOpenDialog(true);
    }
  }, [errorMsg])

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleScan = () => {
    setIsScanning(true);
    listEvowalks()
      .then(res => {
        console.log('EvoWalk API Response:', res);
        if (res.status == 'success') {
          const devices = evowalkDevicesFromList(res);
          console.log('EvoWalk Devices:', devices.map((device) => evowalkLabel(device)));
          setEvowalks(devices);
          // Remember connected units so Record page can detect drop-offs.
          rememberConnectedEvowalks(devices);
        } else {
          console.error('EvoWalk API Error:', res.reason);
          setErrorMsg(res.reason || 'Unknown error during scan');
          setIsScanning(false);
        }
      })
      .catch(error => {
        console.error('EvoWalk API Call Failed:', error);
        setErrorMsg("Failed to connect to the hardware. Please ensure that you followed the instructions.");
        setIsScanning(false);
      })
      .finally(() => setIsScanning(false));
  };


  const connectEvowalkUI = (evowalk) => {
    if (isDeviceConnected(evowalk)) {
      return;
    }
    const rowKey = evowalkRowKey(evowalk, evowalks.indexOf(evowalk));
    setIsConnecting(prevState => ({ ...prevState, [rowKey]: true }));
    setAnyConnecting(true)

    connectEvowalk(evowalkLabel(evowalk), evowalkAddress(evowalk))
      .then(res => {
        if (res.status !== 'success') {
          setErrorMsg(res.reason || 'Unknown error during connection');
        }
        // Battery level will be updated via handleScan() which fetches the latest device list
        handleScan();
        setAnyConnecting(false)
      })
      .catch(err => {
        setErrorMsg(err.message || 'Network error');
      })
      .finally(() => {
        setIsConnecting(prevState => ({ ...prevState, [rowKey]: false }));
        setAnyConnecting(false)
      });
  };

  const disconnectEvowalkUI = (evowalk) => {
    const rowKey = evowalkRowKey(evowalk, evowalks.indexOf(evowalk));
    setIsDisconnecting(prevState => ({ ...prevState, [rowKey]: true }));

    disconnectEvowalk(evowalkLabel(evowalk), evowalkAddress(evowalk))
      .then(res => {
        if (res.status !== 'success') {
          setErrorMsg(res.reason || 'Unknown error during disconnection');
        }
        handleScan();
      })
      .catch(err => {
        setErrorMsg(err.message || 'Network error');
      })
      .finally(() => {
        setIsDisconnecting(prevState => ({ ...prevState, [rowKey]: false }));
      });
  };

  const removeEvowalkUI = (evowalk) => {
    const rowKey = evowalkRowKey(evowalk, evowalks.indexOf(evowalk));
    setIsRemoving(prevState => ({ ...prevState, [rowKey]: true }));
    removeEvowalk(evowalkLabel(evowalk), evowalkAddress(evowalk))
      .then(res => {
        // HTTP 200 with status "failure" (including an ambiguous name) is still a failed remove.
        if (res.status !== 'success') {
          setErrorMsg(res.reason || 'Unknown error during removal');
        }
        handleScan();
      })
      .catch(err => {
        setErrorMsg(err.message || 'Network error');
      })
      .finally(() => {
        setIsRemoving(prevState => ({ ...prevState, [rowKey]: false }));
      });
  };

  function EvoWalkTable(data) {
    return (
      <section className='patient-table container'>
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan="6" style={{ height: "fit-content" }}>
                <input id='search-bar' placeholder="Search..." onChange={e => setQuery(e.target.value)}></input>
              </td>
            </tr>
            <tr>
              <th>EvoWalk<sup>™</sup> ID</th>
              <th>Availability</th>
              <th>Status</th>
              <th>Battery Level</th>
              <th>Connect</th>
              <th>Remove</th>
            </tr>
            {isScanning ? (
              // Show 2 skeleton rows while scanning
              Array.from({ length: 2 }).map((_, index) => (
                <tr key={`skeleton-${index}`} className="skeleton-row">
                  <td><div className="skeleton-cell" style={{ width: '80%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '60%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '70%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '50%' }}></div></td>
                  <td><div className="skeleton-button"></div></td>
                  <td><div className="skeleton-button"></div></td>
                </tr>
              ))
            ) : evowalks.length > 0 ? (
              evowalks.map((evowalk, index) => {
                const label = evowalkLabel(evowalk);
                const rowKey = evowalkRowKey(evowalk, index);
                const connected = isDeviceConnected(evowalk);
                // Get battery level directly from the device object (battery_percent field)
                // Battery level ranges from 0-10
                let batteryLevel = null;
                
                if (evowalk.battery_percent !== undefined && evowalk.battery_percent !== null) {
                  batteryLevel = evowalk.battery_percent;
                  console.log(`EvoWalk ${label} battery level:`, batteryLevel);
                }
                
                // Function to render battery icon based on level (0-10)
                const renderBatteryIcon = () => {
                  if (batteryLevel === null) {
                    return '-';
                  }
                  
                  // Battery level ranges from 0 to 10
                  // Full: 8-10, Half: 4-7, Low: 1-3, Dead: 0
                  if (batteryLevel >= 8) {
                    return <IoBatteryFull style={{ fontSize: '28px', color: '#5ab5bb' }} title={`Battery: ${batteryLevel}/10`} />;
                  } else if (batteryLevel >= 4) {
                    return <IoBatteryHalf style={{ fontSize: '28px', color: '#5ab5bb' }} title={`Battery: ${batteryLevel}/10`} />;
                  } else if (batteryLevel >= 1) {
                    return <IoBatteryHalf style={{ fontSize: '28px', color: '#5ab5bb', opacity: 0.6 }} title={`Battery: ${batteryLevel}/10`} />;
                  } else {
                    return <IoBatteryDead style={{ fontSize: '28px', color: '#5ab5bb' }} title={`Battery: ${batteryLevel}/10`} />;
                  }
                };
                
                return (
                <tr key={rowKey} style={{ backgroundColor: (evowalk.available === 'yes' && connected) ? 'green' : 'inherit' }}>
                  <td>
                    <span className="evowalk-id-cell">
                      <span>{label}</span>
                      {isNewEvowalkDevice(evowalk) ? (
                        <span className="evowalk-new-device-tag">New device</span>
                      ) : null}
                    </span>
                  </td>
                  <td>{evowalk.available ? "yes" : "no"}</td>
                  <td style={{ color: connected ? 'green' : 'inherit' }}>
                    {connected ? "Connected" : "Not Connected"}
                  </td>
                  <td>
                    {renderBatteryIcon()}
                  </td>
                  <td>
                    {connected ? (
                      <button
                        className='disconnect-button'
                        onClick={() => disconnectEvowalkUI(evowalk)}>
                        {isDisconnecting[rowKey] ? (
                          <span className='spinner'></span>
                        ) : (
                          "Disconnect"
                        )}
                      </button>
                    ) : (
                      <button
                        disabled={anyConnecting}
                        className='connect-button'
                        onClick={() => connectEvowalkUI(evowalk)}>
                        {isConnecting[rowKey] ? (
                          <span className='spinner'></span>
                        ) : (
                          "Connect"
                        )}
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      className='remove-button'
                      onClick={() => removeEvowalkUI(evowalk)}>
                      {isRemoving[rowKey] ? (
                        <span className='spinner'></span>
                      ) : (
                        "Remove"
                      )}
                    </button>
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="evowalk-empty-state">
                  <div className="evowalk-empty-state-inner">
                    <p className="evowalk-empty-title">No EvoWalk devices found</p>
                    <p className="evowalk-empty-hint">
                      Click <strong>Scan</strong> to search for devices. Ensure hardware is powered and paired per your setup guide.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    );
  }

  const styles = {
    marginTop: '30px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
  };

  return (
    <div className='parent-container'>
      <div className='container'>
        <h1 className='page-title'>
          EvoWalk<sup>™</sup>
        </h1>
      </div>

      <div style={styles}>
        <button disabled={isScanning} className='scan-button' onClick={handleScan}>{!isScanning ? 'Scan' : <span className='spinner'></span>}</button>
      </div>
      <EvoWalkTable data={evowalks} />
      {/* pop up error */}
      <Box>
        <Dialog
          TransitionComponent={Fade}
          transitionDuration={{ enter: 260, exit: 180 }}
          open={openDialog}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Error"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {errorMsg}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <CommonButton onClick={handleDialogClose}>Close</CommonButton>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
}

export default EvoWalkPage;

